// Validation utilities for AeroZeep

/**
 * Validate Nigerian BVN (Bank Verification Number)
 * BVN is exactly 11 digits
 */
export function validateBVN(bvn: string): { valid: boolean; error?: string } {
  const cleaned = bvn.replace(/\s/g, '')

  if (!cleaned) {
    return { valid: false, error: 'BVN is required' }
  }

  if (!/^\d+$/.test(cleaned)) {
    return { valid: false, error: 'BVN must contain only numbers' }
  }

  if (cleaned.length !== 11) {
    return { valid: false, error: 'BVN must be exactly 11 digits' }
  }

  return { valid: true }
}

/**
 * Validate Nigerian bank account number
 * Account numbers are typically 10 digits (NUBAN format)
 */
export function validateAccountNumber(accountNumber: string): {
  valid: boolean
  error?: string
} {
  const cleaned = accountNumber.replace(/\s/g, '')

  if (!cleaned) {
    return { valid: false, error: 'Account number is required' }
  }

  if (!/^\d+$/.test(cleaned)) {
    return { valid: false, error: 'Account number must contain only numbers' }
  }

  if (cleaned.length !== 10) {
    return { valid: false, error: 'Account number must be exactly 10 digits' }
  }

  return { valid: true }
}

/**
 * Validate ticket/PNR number
 * PNR is typically 6 alphanumeric characters
 * Ticket number is typically 13 digits
 */
export function validateTicketNumber(value: string): {
  valid: boolean
  error?: string
  type?: 'pnr' | 'ticket'
} {
  const cleaned = value.replace(/\s/g, '').toUpperCase()

  if (!cleaned) {
    return { valid: false, error: 'Ticket or PNR number is required' }
  }

  // Check if it's a PNR (6 alphanumeric characters)
  if (/^[A-Z0-9]{6}$/.test(cleaned)) {
    return { valid: true, type: 'pnr' }
  }

  // Check if it's a ticket number (13 digits)
  if (/^\d{13}$/.test(cleaned)) {
    return { valid: true, type: 'ticket' }
  }

  return {
    valid: false,
    error: 'Enter a valid PNR (6 characters) or ticket number (13 digits)',
  }
}

/**
 * Validate Nigerian phone number
 * Formats: +234XXXXXXXXXX, 234XXXXXXXXXX, 0XXXXXXXXXX
 */
export function validatePhoneNumber(phone: string): {
  valid: boolean
  error?: string
  formatted?: string
} {
  const cleaned = phone.replace(/[\s\-\(\)]/g, '')

  if (!cleaned) {
    return { valid: false, error: 'Phone number is required' }
  }

  // Check various formats
  let normalized: string

  if (cleaned.startsWith('+234') && cleaned.length === 14) {
    normalized = cleaned
  } else if (cleaned.startsWith('234') && cleaned.length === 13) {
    normalized = '+' + cleaned
  } else if (cleaned.startsWith('0') && cleaned.length === 11) {
    normalized = '+234' + cleaned.slice(1)
  } else {
    return { valid: false, error: 'Enter a valid Nigerian phone number' }
  }

  // Validate the number part
  const numberPart = normalized.slice(4)
  if (!/^[789]\d{9}$/.test(numberPart)) {
    return { valid: false, error: 'Enter a valid Nigerian phone number' }
  }

  return { valid: true, formatted: normalized }
}

/**
 * Validate email address
 */
export function validateEmail(email: string): {
  valid: boolean
  error?: string
} {
  const cleaned = email.trim().toLowerCase()

  if (!cleaned) {
    return { valid: false, error: 'Email is required' }
  }

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  if (!emailRegex.test(cleaned)) {
    return { valid: false, error: 'Enter a valid email address' }
  }

  return { valid: true }
}

/**
 * Validate required field
 */
export function validateRequired(
  value: string,
  fieldName: string,
): { valid: boolean; error?: string } {
  if (!value || !value.trim()) {
    return { valid: false, error: `${fieldName} is required` }
  }
  return { valid: true }
}

/**
 * Format BVN for display (XXX-XXXX-XXXX)
 */
export function formatBVN(bvn: string): string {
  const cleaned = bvn.replace(/\D/g, '').slice(0, 11)
  if (cleaned.length <= 3) return cleaned
  if (cleaned.length <= 7) return `${cleaned.slice(0, 3)}-${cleaned.slice(3)}`
  return `${cleaned.slice(0, 3)}-${cleaned.slice(3, 7)}-${cleaned.slice(7)}`
}

/**
 * Format account number for display (XXX-XXX-XXXX)
 */
export function formatAccountNumber(accountNumber: string): string {
  const cleaned = accountNumber.replace(/\D/g, '').slice(0, 10)
  if (cleaned.length <= 3) return cleaned
  if (cleaned.length <= 6) return `${cleaned.slice(0, 3)}-${cleaned.slice(3)}`
  return `${cleaned.slice(0, 3)}-${cleaned.slice(3, 6)}-${cleaned.slice(6)}`
}
